import React from "react";
import { EditableCellEditContainer } from "./editable-cell-edit-container";
import { EditableCellDisplayContainer } from "./editable-cell-display-container";
import { canEditCell, isTextareaField } from "./editable-cell-permissions";
import type {
  SimpleFieldConfig,
  FieldValue,
  BaseEntity,
  CellEditHandler,
  CellSaveHandler,
  CellCancelHandler,
  UniversalInlineEditHandler,
} from "../types/index";
import type { AuthenticatedUser } from "@/lib/types/unified";

/**
 * Main orchestrator for rendering editable cells
 * Delegates to display or edit containers based on editing state
 * Maintains exact same API as original implementation
 */
export const renderEditableCell = <T extends BaseEntity = BaseEntity>(
  value: FieldValue,
  fieldConfig: SimpleFieldConfig,
  rowId: string,
  fieldId: string,
  editingCell: { rowId: string; fieldId: string } | null,
  editValue: string,
  onEdit: CellEditHandler,
  onSave: CellSaveHandler,
  onCancel: CellCancelHandler,
  setEditValue: (value: string) => void,
  data: T[],
  onInlineEdit: UniversalInlineEditHandler,
  appUser?: AuthenticatedUser,
  isTitleField?: boolean,
  customDisplayText?: string,
  userRole?: string,
  entityName?: string
): React.ReactNode => {
  const isEditing = editingCell?.rowId === rowId && editingCell?.fieldId === fieldId;

  // Check if user can edit this cell
  const canEdit = canEditCell(fieldConfig, userRole, entityName);

  // Check if this is a textarea field
  const isTextarea = isTextareaField(fieldConfig, fieldId);

  // Render edit mode
  if (isEditing) {
    return (
      <EditableCellEditContainer<T>
        value={value}
        fieldConfig={fieldConfig}
        rowId={rowId}
        fieldId={fieldId}
        editValue={editValue}
        isTextareaField={isTextarea}
        data={data}
        onSave={onSave}
        onCancel={onCancel}
        setEditValue={setEditValue}
        onInlineEdit={onInlineEdit}
      />
    );
  }

  // Render display mode
  return (
    <EditableCellDisplayContainer<T>
      value={value}
      fieldConfig={fieldConfig}
      rowId={rowId}
      fieldId={fieldId}
      canEdit={canEdit}
      isTitleField={isTitleField}
      customDisplayText={customDisplayText}
      data={data}
      entityType={entityName}
      onEdit={onEdit}
    />
  );
};
